import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FixItAllPanel from '../../components/remediation/FixItAllPanel';
import { useAuth } from '../../context/AuthContext';
import { oauthAPI, microsoftAPI, googleAPI } from '../../services/api';

export default function DashboardPage() {
  const { tenant } = useAuth();
  const [connections, setConnections] = useState({ microsoft: { connected: false }, google: { connected: false } });
  const [microsoftMfaSummary, setMicrosoftMfaSummary] = useState(null);
  const [googleMfaSummary, setGoogleMfaSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await oauthAPI.getStatus();
      setConnections(response.data);

      // If Microsoft is connected, load MFA summary
      if (response.data.microsoft?.connected) {
        try {
          const mfaRes = await microsoftAPI.getMFASummary();
          setMicrosoftMfaSummary(mfaRes.data.data);
        } catch (err) {
          console.error('Failed to load Microsoft MFA summary:', err);
        }
      }

      // If Google is connected, load MFA summary
      if (response.data.google?.connected) {
        try {
          const mfaRes = await googleAPI.getMFASummary();
          setGoogleMfaSummary(mfaRes.data.data);
        } catch (err) {
          console.error('Failed to load Google MFA summary:', err);
        }
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasConnections = connections.microsoft.connected || connections.google.connected;

  // Calculate combined stats
  const getCombinedStats = () => {
    let totalUsers = 0;
    let mfaEnabled = 0;
    let mfaDisabled = 0;

    if (microsoftMfaSummary) {
      totalUsers += microsoftMfaSummary.totalUsers;
      mfaEnabled += microsoftMfaSummary.mfaEnabled;
      mfaDisabled += microsoftMfaSummary.mfaDisabled;
    }

    if (googleMfaSummary) {
      totalUsers += googleMfaSummary.totalUsers;
      mfaEnabled += googleMfaSummary.mfaEnabled;
      mfaDisabled += googleMfaSummary.mfaDisabled;
    }

    const mfaPercentage = totalUsers > 0 ? Math.round((mfaEnabled / totalUsers) * 100) : 0;

    return { totalUsers, mfaEnabled, mfaDisabled, mfaPercentage };
  };

  const combinedStats = getCombinedStats();
  const hasMfaData = microsoftMfaSummary || googleMfaSummary;

  // Calculate security score based on MFA coverage
  const getSecurityScore = () => {
    if (!hasMfaData) return null;
    return combinedStats.mfaPercentage;
  };

  const securityScore = getSecurityScore();

  // Calculate risks (users without MFA)
  const getRisks = () => {
    if (!hasMfaData) return 0;
    return combinedStats.mfaDisabled;
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Welcome back{tenant?.name ? `, ${tenant.name.split("'")[0]}` : ''}
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Here's an overview of your security posture.
          </p>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <LoadingSpinner size="lg" text="Loading dashboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          Welcome back{tenant?.name ? `, ${tenant.name.split("'")[0]}` : ''}
        </h1>
        <p className="mt-1 text-slate-500 dark:text-slate-400">
          Here's an overview of your security posture.
        </p>
      </div>

      {/* Connection Status Banner */}
      {!hasConnections && (
        <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-6">
          <div className="flex items-start gap-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                Connect your cloud workspace
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Connect Microsoft 365 or Google Workspace to start auditing your security posture.
              </p>
              <Link to="/dashboard/connect" className="mt-3 inline-block">
                <Button size="sm">
                  Connect Now
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* One-click remediation — Fix It All */}
      {hasConnections && <FixItAllPanel />}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <div className="flex items-center gap-4">
            <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              securityScore !== null
                ? securityScore >= 80
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : securityScore >= 50
                    ? 'bg-amber-100 dark:bg-amber-900/30'
                    : 'bg-red-100 dark:bg-red-900/30'
                : 'bg-primary-100 dark:bg-primary-900/30'
            }`}>
              <ShieldCheckIcon className={`h-6 w-6 ${
                securityScore !== null
                  ? securityScore >= 80
                    ? 'text-green-600 dark:text-green-400'
                    : securityScore >= 50
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-red-600 dark:text-red-400'
                  : 'text-primary-600 dark:text-primary-400'
              }`} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">MFA Coverage</p>
              <p className={`text-2xl font-bold ${
                securityScore !== null
                  ? securityScore >= 80
                    ? 'text-green-600 dark:text-green-400'
                    : securityScore >= 50
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-red-600 dark:text-red-400'
                  : 'text-slate-900 dark:text-white'
              }`}>
                {securityScore !== null ? `${securityScore}%` : hasConnections ? '--' : 'N/A'}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
              <LinkIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Connected</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {[connections.microsoft.connected, connections.google.connected].filter(Boolean).length} / 2
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              getRisks() > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'
            }`}>
              {getRisks() > 0 ? (
                <ShieldExclamationIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              ) : (
                <ShieldCheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              )}
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Users Without MFA</p>
              <p className={`text-2xl font-bold ${
                getRisks() > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
              }`}>
                {hasMfaData ? getRisks() : hasConnections ? '--' : 'N/A'}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <UserGroupIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Total Users</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {hasMfaData ? combinedStats.totalUsers : hasConnections ? '--' : 'N/A'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* MFA Overview (when Microsoft connected) */}
      {microsoftMfaSummary && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Microsoft 365 - MFA Status
            </h2>
            <Link to="/dashboard/mfa">
              <Button size="sm" variant="outline">
                View Details
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <Card>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {microsoftMfaSummary.mfaEnabled}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">MFA Enabled</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                  {microsoftMfaSummary.mfaDisabled}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">MFA Disabled</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                  {microsoftMfaSummary.mfaPercentage}%
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Coverage Rate</p>
              </div>
            </div>
            {microsoftMfaSummary.lastSyncedAt && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-4 text-center">
                Last synced: {new Date(microsoftMfaSummary.lastSyncedAt).toLocaleString()}
              </p>
            )}
          </Card>
        </div>
      )}

      {/* MFA Overview (when Google connected) */}
      {googleMfaSummary && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Google Workspace - 2-Step Verification Status
            </h2>
            <Link to="/dashboard/mfa">
              <Button size="sm" variant="outline">
                View Details
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <Card>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {googleMfaSummary.mfaEnabled}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">2SV Enrolled</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                  {googleMfaSummary.mfaDisabled}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">2SV Not Enrolled</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {googleMfaSummary.mfaEnforced || 0}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">2SV Enforced</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                  {googleMfaSummary.mfaPercentage}%
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Coverage Rate</p>
              </div>
            </div>
            {googleMfaSummary.lastSyncedAt && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-4 text-center">
                Last synced: {new Date(googleMfaSummary.lastSyncedAt).toLocaleString()}
              </p>
            )}
          </Card>
        </div>
      )}

      {/* Connection Status Cards */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Connected Platforms
        </h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Microsoft */}
          <Card>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <svg className="h-6 w-6" viewBox="0 0 23 23">
                    <path fill="#f35325" d="M1 1h10v10H1z" />
                    <path fill="#81bc06" d="M12 1h10v10H12z" />
                    <path fill="#05a6f0" d="M1 12h10v10H1z" />
                    <path fill="#ffba08" d="M12 12h10v10H12z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Microsoft 365</h3>
                  {connections.microsoft.connected ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {connections.microsoft.providerEmail}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">Not connected</p>
                  )}
                </div>
              </div>
              <div>
                {connections.microsoft.connected ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-3 py-1 text-sm font-medium text-green-700 dark:text-green-400">
                    Connected
                  </span>
                ) : (
                  <Link to="/dashboard/connect">
                    <Button size="sm" variant="outline">Connect</Button>
                  </Link>
                )}
              </div>
            </div>
          </Card>

          {/* Google */}
          <Card>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Google Workspace</h3>
                  {connections.google.connected ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {connections.google.providerEmail}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">Not connected</p>
                  )}
                </div>
              </div>
              <div>
                {connections.google.connected ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-3 py-1 text-sm font-medium text-green-700 dark:text-green-400">
                    Connected
                  </span>
                ) : (
                  <Link to="/dashboard/connect">
                    <Button size="sm" variant="outline">Connect</Button>
                  </Link>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Link to="/dashboard/mfa">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center gap-3">
                <ShieldCheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <span className="font-medium text-slate-900 dark:text-white">MFA Status</span>
              </div>
            </Card>
          </Link>
          <Link to="/dashboard/devices">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center gap-3">
                <DevicePhoneMobileIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <span className="font-medium text-slate-900 dark:text-white">Device Inventory</span>
              </div>
            </Card>
          </Link>
          <Link to="/dashboard/connect">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center gap-3">
                <LinkIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <span className="font-medium text-slate-900 dark:text-white">Connect Platform</span>
              </div>
            </Card>
          </Link>
          <Link to="/dashboard/settings">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center gap-3">
                <UserGroupIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <span className="font-medium text-slate-900 dark:text-white">Account Settings</span>
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}
